<template>
  <ErrorBoundary>
    <label class="text-sm font-medium block mt-2">
      {{ $t('product.show.checkout.multidateSelectLabel') }}
    </label>
    <Select
      v-model="selectedDate"
      class="mt-2 rounded-t-lg rounded-b-lg"
      :options="dates"
      :loading-more-options="publicEventDatePending"
      :filter-selected="false"
      :position-absolute="true"
      :separate-dropdown="true"
      :transformer-fn="
        (option: EventDate) => ({
          id: option.id,
          value: option,
          label: fnGetDateLabelHtml(option),
          disabled: !eventDateGetters.getDateIsOpen(option),
          innerHTML: eventDateGetters.getChildren(option),
        })
      "
      size="small"
      :placeholder="$t('product.show.checkout.multidateSelectPlaceholder')"
      @infinite-scroll-callback="datesPagination.goToNextPage"
      @update:open="handleSelectOpen"
    >
      <template #label="option">
        <EventBuyBoxDateOption
          :option-data="option.value"
          :always-show-multidate="true"
          :show-collapse-button="false"
          :max-children="0"
        />
      </template>
      <template #option="option">
        <client-only>
          <EventBuyBoxDateOption
            :option-data="option.value"
            :show-collapse-button="true"
          />
        </client-only>
      </template>
    </Select>
  </ErrorBoundary>
</template>
<script setup lang="ts">
import type { EventDate } from '~/composables/types/event'
import {
  pickPreferredOrFirstAvailableDate,
  usePreferredDateIdFromQuery,
} from '~/composables/usePreferredDateIdFromQuery'
const { eventId } = defineProps<{ eventId: string }>()
const { fnGetDateLabelHtml } = useEventDateHelpers()
const selectedDate = ref<EventDate | undefined>(undefined)
const preferredDateId = usePreferredDateIdFromQuery()

const {
  pagination: datesPagination,
  pending: publicEventDatePending,
  accumulatedDates: dates,
} = await useFetchEventDates(eventId, {
  accumulateDates: true,
  query: {
    include: ['children'],
  },
})

const emit = defineEmits(['update-selected-date'])

const openDateId = ref<string | undefined>(undefined)
provide('multidateOpenDateId', openDateId)
provide('setMultidateOpenDateId', (id: string | undefined) => {
  openDateId.value = id
})

provide('multidateSelectedDate', selectedDate)
provide('setMultidateSelectedDate', (date: EventDate) => {
  selectedDate.value = date
  emit('update-selected-date', date)
})

onMounted(() => {
  if (!preferredDateId.value) return

  const dateToSelect = pickPreferredOrFirstAvailableDate(
    dates.value || [],
    preferredDateId.value
  )

  if (dateToSelect?.id !== preferredDateId.value) return

  selectedDate.value = dateToSelect
  emit('update-selected-date', dateToSelect)
})

const handleSelectOpen = (open: boolean) => {
  if (open) openDateId.value = selectedDate.value?.id ?? dates.value?.[0]?.id
}
</script>
